import type { ComponentType, ReactElement } from 'react';
import type { AuthFlow } from '@shared/utils/analytics/events';
import { readAuthDesignDecision } from '@shared/utils/authDesignDecision';
import type { AuthErrorMessage } from '@shared/utils/authErrorMessage';
import { ARKOSE_GATE_VERIFY_OR_BYPASS_INTERACTION_IDS } from './interactions';
import { isArkoseGateVerifyOrBypassScreen } from './screenMapping';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;

type DescopeRecord = Record<string, unknown>;

type DescopeFlowState = DescopeRecord & {
  screenName?: string;
  form?: DescopeRecord;
  error?: { text?: string };
  next?: DescopeNext;
};

export interface ArkoseGateScreenProps {
  authFlow: AuthFlow;
  backHref?: string;
  loginHref?: string;
  onContinue: () => Promise<void>;
  authError: AuthErrorMessage;
  newDesign?: boolean;
}

export interface ArkoseGateVerifyOrBypassRouteScreenProps {
  screenName?: string;
  state: DescopeFlowState;
  form: DescopeRecord;
  smsAuthForm?: DescopeRecord;
  loginBackHref: string;
  loginHref?: string;
  signupBackHref: string;
  ArkoseGateScreen: ComponentType<ArkoseGateScreenProps>;
  authError: AuthErrorMessage;
  newDesign?: boolean;
  shouldHoldSignup?: boolean;
  isSignupHoldContext?: boolean;
}

const getStringField = (
  record: DescopeRecord | undefined,
  field: string
): string | undefined => {
  const value = record?.[field];
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
};

const getRecordField = (
  record: DescopeRecord | undefined,
  field: string
): DescopeRecord | undefined => {
  const value = record?.[field];
  return value && typeof value === 'object'
    ? (value as DescopeRecord)
    : undefined;
};

const getHashRoute = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const [routeWithoutQuery = ''] = window.location.hash.split('?');
  return routeWithoutQuery.replace(/^#/, '').replace(/\/$/, '') || '/';
};

export const getArkoseGateVerifyOrBypassAuthFlow = (
  state: DescopeRecord | undefined,
  form: DescopeRecord | undefined
): AuthFlow => {
  const isSignupRoute = ['/register', '/claim'].includes(getHashRoute());
  const stateForm = getRecordField(state, 'form');
  const smsAuthFlow =
    getStringField(stateForm, 'form.smsAuthFlow') ||
    getStringField(stateForm, 'smsAuthFlow') ||
    getStringField(form, 'form.smsAuthFlow') ||
    getStringField(form, 'smsAuthFlow');

  if (smsAuthFlow === 'signup' || smsAuthFlow === 'login') {
    return smsAuthFlow;
  }

  const stateSignupEmail = getStringField(stateForm, 'email');
  const stateLoginIdentifier = getStringField(
    stateForm,
    'form.emailOrUsername'
  );
  const formSignupEmail = getStringField(form, 'email');
  const formLoginIdentifier = getStringField(form, 'form.emailOrUsername');
  const deriveIdentifierAuthFlow = (
    signupEmail: string | undefined,
    loginIdentifier: string | undefined
  ): AuthFlow | undefined => {
    if (signupEmail && loginIdentifier) {
      return undefined;
    }
    if (loginIdentifier) {
      return 'login';
    }
    if (signupEmail) {
      return 'signup';
    }
    return undefined;
  };

  const stateAuthFlow = deriveIdentifierAuthFlow(
    stateSignupEmail,
    stateLoginIdentifier
  );
  if (stateSignupEmail || stateLoginIdentifier) {
    return stateAuthFlow || (isSignupRoute ? 'signup' : 'login');
  }

  const formAuthFlow = deriveIdentifierAuthFlow(
    formSignupEmail,
    formLoginIdentifier
  );
  if (formSignupEmail || formLoginIdentifier) {
    return formAuthFlow || (isSignupRoute ? 'signup' : 'login');
  }

  return isSignupRoute ? 'signup' : 'login';
};

export const routeArkoseGateVerifyOrBypassScreen = ({
  screenName,
  state,
  form,
  smsAuthForm = form,
  loginBackHref,
  loginHref,
  signupBackHref,
  ArkoseGateScreen,
  authError,
  newDesign,
  shouldHoldSignup = false,
  isSignupHoldContext = false,
}: ArkoseGateVerifyOrBypassRouteScreenProps): ReactElement | null => {
  if (!screenName || !isArkoseGateVerifyOrBypassScreen(screenName)) {
    return null;
  }

  // App's classification is authoritative because it also sees local signup
  // state that is absent from some echoed gate forms.
  const displayAuthFlow =
    isSignupHoldContext ||
    getArkoseGateVerifyOrBypassAuthFlow(state, form) === 'signup'
      ? 'signup'
      : 'login';
  const stateForm = getRecordField(state, 'form');
  const stateSmsAuthFlow =
    getStringField(stateForm, 'form.smsAuthFlow') ||
    getStringField(stateForm, 'smsAuthFlow');
  const usesSmsAuthForm = stateSmsAuthFlow === 'login';
  const payload = usesSmsAuthForm ? smsAuthForm : form;
  const resolvedNewDesign = newDesign ?? readAuthDesignDecision();

  const content = (
    <ArkoseGateScreen
      authFlow={displayAuthFlow}
      backHref={displayAuthFlow === 'signup' ? signupBackHref : loginBackHref}
      loginHref={loginHref}
      onContinue={async () => {
        if (state.next) {
          await state.next(
            ARKOSE_GATE_VERIFY_OR_BYPASS_INTERACTION_IDS.CONTINUE,
            payload
          );
        }
      }}
      authError={authError}
      newDesign={resolvedNewDesign}
    />
  );

  const isHeld = isSignupHoldContext && shouldHoldSignup;

  return (
    <div
      className={
        isHeld ? 'pointer-events-none fixed inset-0 opacity-0' : undefined
      }
      aria-hidden={isHeld}
    >
      {content}
    </div>
  );
};
